import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { compose } from "redux";
import { AccountType, withAccessControl, SMART_BANNER_CONTAINER_ID, UniversalAppShell } from "component-library";
import { useZendesk } from "shared-hooks";
import { LaunchDarklyProvider } from "shared-services";
import { IUser } from "shared/models/user";
import { MapDispatchToProps } from "shared/types/utilities";
import { IApplicationState } from "store";
import { getCommonTranslation } from "store/translation/translation.actions";
import { GetCommonTranslation } from "store/translation/translation.types";
import {
  hidePopup,
  setLoading,
  setLocale,
  setPage404,
} from "store/ui/ui.actions";
import {
  HidePopup,
  IUIState,
  SetLoading,
  SetLocale,
  SetPage404,
} from "store/ui/ui.types";

import { getUserData } from "store/user/user.actions";
import changeTrackingRegistryService from "services/changeTrackingRegistryService";

import Content from "app-shell/Content/Content";
import Page404Container from "app-shell/Page404Container/Page404Container";

import withUserData, {
  IWithUserActions,
  IWithUserStoreProps,
} from "hoc/withUserData";
import withGlobalSpinner from "hoc/withGlobalSpinner";
import withLocalizedData from "hoc/withLocalizedData";
import withNavigator from "hoc/withNavigator";
import withUnsavedChangesNavigationPrevention from "hoc/withUnsavedChangesNavigationPrevention";

import Popups from "components/Popups/Popups";

export interface IAppActions extends IWithUserActions {
  setLoading: SetLoading;
  setLocale: SetLocale;
  hidePopup: HidePopup;
  getCommonTranslation: GetCommonTranslation;
  setPage404: SetPage404;
}

export interface IAppStoreProps extends IWithUserStoreProps {
  ui: IUIState;
  locale: string;
  userType?: AccountType;
  isLoading: boolean;
  isCommonTranslationLoaded: boolean;
  isPage404: boolean;
  userId?: string;
  additionalInfo: IUser;
}

export interface IAppProps
  extends IAppStoreProps,
    IAppActions,
    RouteComponentProps {}

export const App: React.FC<IAppProps> = ({
  isPage404,
  location,
  setPage404,
}) => {
  useZendesk(location.pathname);

  const prevPathName = useRef(location.pathname);

  useEffect(() => {
    if (isPage404 && prevPathName.current !== location.pathname) {
      setPage404(false);
    }

    prevPathName.current = location.pathname;
  }, [isPage404, location.pathname, setPage404]);

  if (isPage404) {
    return <Page404Container />;
  }

  return (
    <LaunchDarklyProvider>
      <div id={SMART_BANNER_CONTAINER_ID} />
      <UniversalAppShell>
        <Content />
      </UniversalAppShell>
      <Popups />
    </LaunchDarklyProvider>
  );
};

const mapDispatchToProps: MapDispatchToProps<IAppActions> = {
  setLoading,
  setLocale,
  hidePopup,
  getCommonTranslation,
  getUserData,
  setPage404,
};

const mapStateToProps = (state: IApplicationState): IAppStoreProps => ({
  ui: state.ui,
  locale: state.ui.locale,
  isLoading: state.ui.isLoading,
  isCommonTranslationLoaded: state.translation.isCommonLoaded,
  isUserLoaded: state.user.isLoaded,
  userType: state.user.data?.type,
  isPage404: state.ui.isPage404,
  userId: state.user.data?.id,
  additionalInfo: state.user.data!,
});

export default compose(
  withRouter,
  withUnsavedChangesNavigationPrevention(
    window,
    changeTrackingRegistryService.getUnsavedChange
  ),
  connect(mapStateToProps, mapDispatchToProps),
  withNavigator,
  withGlobalSpinner,
  withUserData,
  withLocalizedData,
  withAccessControl({
    accountTypes: [AccountType.Performer, AccountType.Agent],
  })
)(App) as React.ComponentType;
